import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import { Checkbox } from '../ui/checkbox';
import {
  AlertTriangle, Loader2, RefreshCw, CheckCircle2,
  Zap, PlusCircle, User, UserCog, Users,
} from 'lucide-react';
import { toast } from 'sonner';

/**
 * QA Mode control panel (iter92).
 *
 * Renders only when the backend `/payments/dev/reset-billing/status`
 * says `available:true` AND the caller is a QA/demo/seeded user, so
 * this component is invisible in production regardless of which
 * account is logged in.
 *
 * Actions:
 *  - Full reset (billing + onboarding + shifts + notifications + ledger)
 *  - One-click plan switch (Trial / PAYG / Monthly / Annual / Multi-site)
 *  - Founding Member toggle for the swap
 *  - "Test Stripe Checkout" toggle:
 *      OFF → instant DB mutation, no Stripe write
 *      ON  → real Stripe Checkout Session redirect
 *  - Simulate a completed PAYG shift (writes a billing_ledger row)
 */
export default function QaModePanel() {
  const { api } = useAuth();
  const [available, setAvailable] = useState(false);
  const [isSandboxUser, setIsSandboxUser] = useState(false);
  const [busy, setBusy] = useState(null);
  const [useCheckout, setUseCheckout] = useState(false);
  const [foundingMember, setFoundingMember] = useState(false);
  const [recreateCustomer, setRecreateCustomer] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [scenario, setScenario] = useState(null);

  const loadScenario = useCallback(async () => {
    try {
      const r = await api.get('/qa/scenario/status');
      setScenario(r.data);
    } catch {
      setScenario(null);
    }
  }, [api]);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/payments/dev/reset-billing/status');
        setAvailable(Boolean(res.data?.available));
        setIsSandboxUser(Boolean(res.data?.isSandboxUser));
        if (res.data?.available && res.data?.isSandboxUser) {
          loadScenario();
        }
      } catch {
        setAvailable(false);
      }
    })();
  }, [api, loadScenario]);

  if (!available || !isSandboxUser) return null;

  const call = async (label, endpoint, body = null) => {
    setBusy(label);
    setLastResult(null);
    try {
      const res = body ? await api.post(endpoint, body) : await api.post(endpoint);
      const msg = res.data?.checkoutUrl
        ? 'Redirecting to Stripe Checkout…'
        : (res.data?.message || 'Done');
      setLastResult(msg);
      toast.success(msg);
      if (res.data?.checkoutUrl) {
        window.location.href = res.data.checkoutUrl;
        return;
      }
      setTimeout(() => window.location.reload(), 1000);
    } catch (err) {
      const detail =
        err?.response?.data?.detail?.message ||
        err?.response?.data?.detail ||
        err?.message ||
        'Action failed';
      toast.error(typeof detail === 'string' ? detail : 'Action failed');
    } finally {
      setBusy(null);
    }
  };

  const setPlan = (plan) => call(
    `plan-${plan}`,
    '/qa/set-plan',
    {
      planType: plan,
      useStripeCheckout: useCheckout && ['monthly', 'annual', 'multisite', 'payg'].includes(plan),
      foundingMember: foundingMember && (plan === 'monthly' || plan === 'trial'),
      originUrl: window.location.origin,
    },
  );

  const plans = [
    { id: 'trial', label: 'Trial (14d)' },
    { id: 'payg', label: 'PAYG' },
    { id: 'monthly', label: 'Monthly' },
    { id: 'annual', label: 'Annual' },
    { id: 'multisite', label: 'Multi-site' },
  ];

  const resetScenario = async (mode, confirmMsg) => {
    if (confirmMsg && !window.confirm(confirmMsg)) return;
    setBusy(`scenario-${mode}`);
    try {
      const r = await api.post('/qa/scenario/reset', { mode });
      setScenario(r.data.scenario);
      toast.success(r.data.message);
      // Whatever scenario reset happened may have wiped the current
      // user's session-relevant fields (branch, plan). Reload so the
      // rest of the app picks up the fresh state.
      setTimeout(() => window.location.reload(), 900);
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || 'Reset failed';
      toast.error(typeof detail === 'string' ? detail : 'Reset failed');
    } finally {
      setBusy(null);
    }
  };

  const impersonate = async (role, path) => {
    setBusy(`login-${role}`);
    try {
      const r = await api.post('/qa/scenario/impersonate', { role });
      const token = r.data?.token;
      if (!token) throw new Error('No token returned');
      // Reuse the same key the AuthContext reads on boot so a reload
      // logs the operator in as the impersonated identity.
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(r.data.user));
      toast.success(`Logged in as ${role}: ${r.data.user.email}`);
      window.location.href = path;
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Impersonation failed');
      setBusy(null);
    }
  };

  const goToShiftCreation = () => impersonate('manager', '/manager/shifts/new');
  const goToWorkerBrowse = () => impersonate('worker', '/worker/shifts');

  return (
    <Card
      className="border-2 border-dashed border-amber-400 bg-amber-50/40"
      data-testid="qa-mode-panel"
    >
      <CardContent className="p-5 space-y-5">
        <div className="flex items-start gap-3">
          <AlertTriangle className="h-6 w-6 text-amber-600 shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="text-base font-bold text-amber-900">QA Mode</h3>
              <span
                className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-500 text-white"
                data-testid="qa-mode-warning-badge"
              >
                Test Mode · Sandbox Only
              </span>
            </div>
            <p className="text-xs text-amber-900/80 mt-1 leading-relaxed">
              One-click controls for repeatable end-to-end testing on this account. Never
              appears in production.
            </p>
          </div>
        </div>

        {/* Full E2E Scenario — paired identities, real UI workflow */}
        <div className="rounded-lg bg-white/70 border border-amber-200 p-3 space-y-3" data-testid="qa-e2e-scenario-section">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <p className="text-sm font-semibold text-slate-900 flex items-center gap-2">
                <Users className="h-4 w-4" /> Full E2E Scenario
              </p>
              <p className="text-xs text-slate-600">
                Paired QA identities that drive the real signup → shift → apply → complete flow.
                Use the buttons below to reset and switch between them.
              </p>
            </div>
            <Button
              size="sm"
              variant="ghost"
              onClick={loadScenario}
              className="text-xs text-slate-600"
              data-testid="qa-scenario-refresh-btn"
            >
              Refresh state
            </Button>
          </div>
          {scenario && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs" data-testid="qa-scenario-status-grid">
              {['manager', 'worker'].map((role) => {
                const s = scenario[role] || {};
                return (
                  <div key={role} className="rounded bg-slate-50 border border-slate-200 p-2">
                    <p className="font-semibold text-slate-900 capitalize flex items-center gap-1">
                      {role === 'manager' ? <UserCog className="h-3 w-3" /> : <User className="h-3 w-3" />}
                      {role} · <span className="font-normal text-slate-600 truncate">{s.email || '(missing)'}</span>
                    </p>
                    <p className="text-slate-600 mt-1">
                      onboarding: <strong>{s.onboardingCompleted ? 'done' : 'pending'}</strong>
                      {role === 'manager' && (<>
                        {' · '}plan: <strong>{s.planType}</strong>{' · '}branches: <strong>{s.branchCount ?? 0}</strong>
                        {' · '}open shifts: <strong>{s.openShiftCount ?? 0}</strong>
                        {' · '}completed: <strong>{s.completedShiftCount ?? 0}</strong>
                      </>)}
                      {role === 'worker' && (
                        <>{' · '}applications: <strong>{s.applicationCount ?? 0}</strong></>
                      )}
                    </p>
                  </div>
                );
              })}
            </div>
          )}
          <div className="flex flex-wrap gap-2 pt-1">
            <Button
              size="sm"
              variant="outline"
              onClick={() => resetScenario('manager', 'Reset the QA Manager identity back to pre-onboarding? Branch + shifts + billing will be cleared.')}
              disabled={busy !== null}
              className="rounded-full border-amber-300 hover:bg-amber-100"
              data-testid="qa-reset-manager-btn"
            >
              {busy === 'scenario-manager' ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <RefreshCw className="h-3.5 w-3.5 mr-1" />}
              Reset Manager
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => resetScenario('worker', 'Reset the QA Worker identity back to pre-onboarding?')}
              disabled={busy !== null}
              className="rounded-full border-amber-300 hover:bg-amber-100"
              data-testid="qa-reset-worker-btn"
            >
              {busy === 'scenario-worker' ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <RefreshCw className="h-3.5 w-3.5 mr-1" />}
              Reset Worker
            </Button>
            <Button
              size="sm"
              onClick={() => resetScenario('full', 'Reset the entire E2E scenario? Both identities go back to pre-onboarding and every shared shift / message / application is deleted.')}
              disabled={busy !== null}
              className="rounded-full bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="qa-reset-full-scenario-btn"
            >
              {busy === 'scenario-full' ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <RefreshCw className="h-3.5 w-3.5 mr-1" />}
              Reset Full E2E Scenario
            </Button>
          </div>
          <div className="flex flex-wrap gap-2 pt-1 border-t border-amber-100">
            <Button
              size="sm"
              variant="secondary"
              onClick={() => impersonate('manager', '/manager/dashboard')}
              disabled={busy !== null}
              className="rounded-full"
              data-testid="qa-login-as-manager-btn"
            >
              {busy === 'login-manager' ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <UserCog className="h-3.5 w-3.5 mr-1" />}
              Login as Manager
            </Button>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => impersonate('worker', '/worker/dashboard')}
              disabled={busy !== null}
              className="rounded-full"
              data-testid="qa-login-as-worker-btn"
            >
              {busy === 'login-worker' ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <User className="h-3.5 w-3.5 mr-1" />}
              Login as Worker
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={goToShiftCreation}
              disabled={busy !== null}
              className="rounded-full text-slate-700"
              data-testid="qa-goto-create-shift-btn"
            >
              → Create Test Shift
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={goToWorkerBrowse}
              disabled={busy !== null}
              className="rounded-full text-slate-700"
              data-testid="qa-goto-worker-browse-btn"
            >
              → Apply as QA Worker
            </Button>
          </div>
          <p className="text-[11px] text-slate-500">
            Full E2E flow uses the real UI — the manager must post the shift and the worker must
            pick it up through the normal screens. Use the simulation tools below for isolated UI checks.
          </p>
        </div>

        {/* Full reset */}
        <div className="rounded-lg bg-white/70 border border-amber-200 p-3">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <p className="text-sm font-semibold text-slate-900">Full account reset</p>
              <p className="text-xs text-slate-600">
                Wipes billing, onboarding, shifts, notifications and PAYG ledger. Keeps login.
              </p>
              <label className="mt-2 flex items-center gap-2 text-xs text-slate-700">
                <Checkbox
                  checked={recreateCustomer}
                  onCheckedChange={setRecreateCustomer}
                  data-testid="qa-recreate-customer-checkbox"
                />
                Also create a fresh Stripe Customer ID
              </label>
            </div>
            <Button
              onClick={() => {
                if (!window.confirm('Reset EVERYTHING on this account? Login and profile are preserved.')) return;
                call('reset', recreateCustomer
                  ? '/payments/dev/reset-billing'
                  : '/qa/reset-account',
                recreateCustomer ? { recreateCustomer: true } : null);
              }}
              disabled={busy !== null}
              className="rounded-full bg-amber-600 hover:bg-amber-700 text-white h-10 px-5"
              data-testid="qa-full-reset-btn"
            >
              {busy === 'reset' ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <RefreshCw className="h-4 w-4 mr-2" />
              )}
              Reset everything
            </Button>
          </div>
        </div>

        {/* Plan switcher */}
        <div className="rounded-lg bg-white/70 border border-amber-200 p-3">
          <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
            <div>
              <p className="text-sm font-semibold text-slate-900">Instant plan switch</p>
              <p className="text-xs text-slate-600">
                Move the account into any plan state for testing.
              </p>
            </div>
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 text-xs text-slate-700" title="Applies to Monthly / Trial only">
                <Checkbox
                  checked={foundingMember}
                  onCheckedChange={setFoundingMember}
                  data-testid="qa-founding-member-checkbox"
                />
                Founding Member
              </label>
              <label className="flex items-center gap-2 text-xs text-slate-700">
                <Switch
                  checked={useCheckout}
                  onCheckedChange={setUseCheckout}
                  data-testid="qa-test-stripe-checkout-toggle"
                />
                Test Stripe Checkout
              </label>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {plans.map((p) => (
              <Button
                key={p.id}
                size="sm"
                variant="outline"
                onClick={() => setPlan(p.id)}
                disabled={busy !== null}
                className="rounded-full border-amber-300 hover:bg-amber-100"
                data-testid={`qa-plan-${p.id}-btn`}
              >
                {busy === `plan-${p.id}` ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />
                ) : (
                  <Zap className="h-3.5 w-3.5 mr-1" />
                )}
                {p.label}
              </Button>
            ))}
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setPlan('none')}
              disabled={busy !== null}
              className="rounded-full text-slate-600"
              data-testid="qa-plan-none-btn"
            >
              Back to No Plan
            </Button>
          </div>
          {useCheckout && (
            <p className="text-[11px] text-emerald-700 mt-2" data-testid="qa-stripe-hint">
              Real Stripe Checkout will be used — you&apos;ll be redirected. Any Monthly / Annual click creates a real sandbox subscription.
            </p>
          )}
        </div>

        {/* Simulate shift */}
        <div className="rounded-lg bg-white/70 border border-amber-200 p-3">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <p className="text-sm font-semibold text-slate-900">Simulate a completed PAYG shift</p>
              <p className="text-xs text-slate-600">
                Instant billing_ledger row + shift record so you can test the Usage Statement UI.
              </p>
            </div>
            <Button
              size="sm"
              onClick={() => call('shift', '/qa/simulate-shift', { branchName: 'QA Test Branch' })}
              disabled={busy !== null}
              className="rounded-full bg-slate-800 hover:bg-slate-900 text-white h-9 px-4"
              data-testid="qa-simulate-shift-btn"
            >
              {busy === 'shift' ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />
              ) : (
                <PlusCircle className="h-3.5 w-3.5 mr-1" />
              )}
              Add £3.99 shift
            </Button>
          </div>
        </div>

        {lastResult && (
          <div
            className="flex items-center gap-2 text-xs text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-md p-2"
            data-testid="qa-mode-last-result"
          >
            <CheckCircle2 className="h-4 w-4" />
            <span>{lastResult}</span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
